import type { AnnouncementCriteria } from '@clockwise/clients/announcements';
import { PillBadge } from '@/components/custom/pill-badge';

/**
 * Single source of truth for criteria labels/colors — reused by the admin
 * badge below, the form sheet's Select options, and the live banner's own
 * background. Three independent copies of this map used to drift out of
 * sync (announcement-form-sheet.tsx had its own `CRITERIA_LABELS`;
 * notification-banner.tsx had its own `CRITERIA_STYLES`); import from here
 * instead of redefining.
 */
export const ANNOUNCEMENT_CRITERIA_LABELS: Record<AnnouncementCriteria, string> = {
  error: 'Error',
  warning: 'Warning',
  information: 'Information',
};

/** `bg-*`/`text-*` only — no border, so the banner (not a bordered pill) can
 *  use these classes directly. `AnnouncementCriteriaBadge` adds its own
 *  `border-transparent` on top. */
export const ANNOUNCEMENT_CRITERIA_COLOR_CLASSES: Record<AnnouncementCriteria, string> = {
  error: 'bg-status-bad text-status-bad-foreground',
  warning: 'bg-status-warn text-status-warn-foreground',
  information: 'bg-status-info text-status-info-foreground',
};

/**
 * Pill badge for an announcement's criteria (Error/Warning/Information).
 * Reuses the app's semantic status tokens so this reads identically to any
 * other bad/warn/info surface rather than inventing a one-off palette.
 */
export function AnnouncementCriteriaBadge({ criteria }: { criteria: AnnouncementCriteria }) {
  return (
    <PillBadge
      variant="outline"
      className={`border-transparent ${ANNOUNCEMENT_CRITERIA_COLOR_CLASSES[criteria]}`}
    >
      {ANNOUNCEMENT_CRITERIA_LABELS[criteria]}
    </PillBadge>
  );
}
